import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import AvatarSelector from '../components/AvatarSelector';
import { 
  User, Mail, Shield, Save, ArrowLeft, Crown, Lock, 
  Phone, MapPin, Link as LinkIcon, Upload, Eye, EyeOff, UserCog, X, Ban
} from 'lucide-react';
import { Switch } from '../components/ui/switch';
import AddressAutocomplete from '../components/AddressAutocomplete';
import MultiCategorySelector from '../components/MultiCategorySelector';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';

const Profile = () => {
  const navigate = useNavigate();
  const { userId } = useParams(); // Get userId from URL params
  const { user, loading: authLoading, refreshUser } = useAuth();
  const [viewedUser, setViewedUser] = useState(null); // User being viewed
  const [isOwnProfile, setIsOwnProfile] = useState(true); // Is viewing own profile?
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    bio: '',
    website: '',
    avatar: '',
    competences: []
  });
  const [privacy, setPrivacy] = useState({
    showPhone: false,
    showAddress: false,
    showEmail: false
  });
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [myOffers, setMyOffers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        navigate('/login');
      } else {
        // Check if viewing own profile or another user's profile
        const viewingOwnProfile = !userId || userId === user.id;
        setIsOwnProfile(viewingOwnProfile);
        
        if (viewingOwnProfile) {
          // Load own profile
          console.log('🔄 Chargement profil personnel');
          setViewedUser(user);
          setProfileData({
            name: user.name || '',
            email: user.email || '',
            phone: user.phone || '',
            address: user.address || '',
            bio: user.bio || '',
            website: user.website || '',
            avatar: user.profile_picture_url || user.avatar || '',
            competences: user.competences || []
          });
          setPrivacy({
            showPhone: user.privacy_settings?.show_phone !== false, 
            showAddress: user.privacy_settings?.show_address !== false,
            showEmail: user.privacy_settings?.show_email !== false
          });
          loadMyOffers();
        } else {
          // Load another user's profile
          console.log('🔄 Chargement profil utilisateur:', userId);
          loadUserProfile(userId);
        }
      }
    }
  }, [user, authLoading, userId, navigate]);
  
  const loadUserProfile = async (targetUserId) => {
    try {
      const response = await api.get(`/users/${targetUserId}`);
      const targetUser = response.data;
      setViewedUser(targetUser);
      setProfileData({
        name: targetUser.name || '',
        email: targetUser.email || '',
        phone: targetUser.phone || '',
        address: targetUser.address || '',
        bio: targetUser.bio || '',
        website: targetUser.website || '',
        avatar: targetUser.profile_picture_url || targetUser.avatar || '',
        competences: targetUser.competences || []
      });
      
      // Load offers by this user
      const offersResponse = await api.get('/offers/all');
      const userOffers = offersResponse.data.filter(o => o.createdBy === targetUserId);
      setMyOffers(userOffers);
    } catch (error) {
      console.error('Error loading user profile:', error);
      toast.error('Erreur', { description: 'Impossible de charger le profil' });
      // Use setTimeout to prevent React DOM error on navigation
      setTimeout(() => navigate('/dashboard'), 100);
    } finally {
      setLoading(false);
    }
  };

  const loadMyOffers = async () => {
    try {
      const response = await api.get('/offers/all');
      const userOffers = response.data.filter(o => o.createdBy === user?.id);
      setMyOffers(userOffers);
    } catch (error) {
      console.error('Error loading offers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarSelect = async (avatarUrl) => {
    try {
      // Save avatar directly to server
      await api.patch('/users/me', { profile_picture_url: avatarUrl });
      await refreshUser();
      
      // Update local state
      setProfileData(prev => ({ ...prev, avatar: avatarUrl }));
      
      toast.success('Avatar mis à jour !', { 
        description: 'Votre avatar a été sauvegardé' 
      });
    } catch (error) {
      console.error('❌ Erreur sauvegarde avatar:', error);
      toast.error('Erreur', { 
        description: 'Impossible de sauvegarder l\'avatar' 
      });
    }
  };

  const handleChange = (field, value) => {
    setProfileData(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    try {
      const updateData = {
        name: profileData.name,
        phone: profileData.phone,
        address: profileData.address,
        bio: profileData.bio,
        website: profileData.website,
        privacy_settings: {
          show_phone: privacy.showPhone,
          show_address: privacy.showAddress,
          show_email: privacy.showEmail
        }
      };
      
      console.log('📤 Envoi données profil...', updateData);
      
      const response = await api.patch('/users/me', updateData);
      console.log('✅ Réponse serveur:', response.data);
      
      // Save competences separately if they changed
      if (profileData.competences && profileData.competences.length > 0) {
        await api.put('/auth/me/competences', profileData.competences);
        console.log('✅ Compétences sauvegardées');
      }
      
      // Refresh user in context
      console.log('🔄 Rafraîchissement utilisateur...');
      await refreshUser();
      console.log('✅ Utilisateur rafraîchi');
      
      toast.success('Profil mis à jour !', {
        description: 'Vos informations ont été sauvegardées avec succès'
      });
    } catch (error) {
      console.error('❌ Erreur sauvegarde profil:', error);
      console.error('❌ Détails:', error.response?.data);
      toast.error('Erreur', { 
        description: error.response?.data?.detail || error.message || 'Impossible de mettre à jour le profil' 
      });
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('Erreur', { description: 'Les mots de passe ne correspondent pas' });
      return;
    }

    if (newPassword.length < 8) {
      toast.error('Erreur', { description: 'Le mot de passe doit contenir au moins 8 caractères' });
      return;
    }

    try {
      toast.success('Mot de passe changé !', {
        description: 'Votre mot de passe a été mis à jour avec succès'
      });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error('Erreur', { description: 'Impossible de changer le mot de passe' });
    }
  };

  // Admin actions for managing other users
  const handlePromoteUser = async (userId, newRole) => {
    try {
      await api.patch(`/users/${userId}/role`, { role: newRole });
      toast.success('Rôle modifié', { 
        description: `L'utilisateur a été promu ${newRole === 'admin' ? 'administrateur' : 'modérateur'}` 
      });
      // Reload user profile to show updated role
      loadUserProfile(userId);
    } catch (error) {
      console.error('Error promoting user:', error);
      toast.error('Erreur', { 
        description: error.response?.data?.detail || 'Impossible de modifier le rôle' 
      });
    }
  };

  const handleRevokeRole = async (userId) => {
    try {
      await api.patch(`/users/${userId}/role`, { role: 'user' });
      toast.success('Rôle révoqué', { 
        description: 'L\'utilisateur est maintenant un utilisateur standard' 
      });
      // Reload user profile to show updated role
      loadUserProfile(userId);
    } catch (error) {
      console.error('Error revoking role:', error);
      toast.error('Erreur', { 
        description: error.response?.data?.detail || 'Impossible de révoquer le rôle' 
      });
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-100 via-rose-100 to-purple-200">
        <Navbar />
        <div className="pt-32 flex items-center justify-center">
          <div className="text-2xl text-gray-600">Chargement...</div>
        </div>
      </div>
    );
  }

  if (!user) return null;
  
  // Use viewedUser if available, otherwise use current user
  const displayedUser = viewedUser || user;

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-100 via-rose-100 to-purple-200">
      <Navbar />
      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <Button variant="ghost" onClick={() => navigate('/')} className="mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour
          </Button>

          {/* Profile Header */}
          <Card className="border-none shadow-xl mb-6 bg-gradient-to-r from-orange-500 via-red-500 to-yellow-500 text-white">
            <CardContent className="pt-6">
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="relative group">
                  <Avatar className="w-32 h-32 border-4 border-white shadow-2xl">
                    <AvatarImage src={displayedUser.profile_picture_url || displayedUser.avatar} />
                    <AvatarFallback className="text-4xl bg-white text-orange-500">
                      {displayedUser.name?.charAt(0) || 'U'}
                    </AvatarFallback>
                  </Avatar>
                </div>
                
                <div className="flex-1 text-center md:text-left">
                  <h1 className="text-4xl font-bold mb-2">{displayedUser.name}</h1>
                  {(isOwnProfile || displayedUser.privacy_settings?.show_email !== false) ? (
                    <p className="text-orange-100 mb-3">{displayedUser.email}</p>
                  ) : (
                    <p className="text-orange-100 mb-3 flex items-center gap-2 justify-center md:justify-start">
                      <EyeOff className="w-4 h-4" />
                      Email masqué
                    </p>
                  )}
                  <Badge className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white border-0 px-4 py-2">
                    {displayedUser.role === 'owner' ? (
                      <>
                        <Crown className="w-4 h-4 mr-2" />
                        👑 Propriétaire
                      </>
                    ) : displayedUser.role === 'admin' ? (
                      <>
                        <Shield className="w-4 h-4 mr-2" />
                        Administrateur
                      </>
                    ) : displayedUser.role === 'moderator' ? (
                      <>
                        <Shield className="w-4 h-4 mr-2" />
                        Modérateur
                      </>
                    ) : (
                      <>
                        <User className="w-4 h-4 mr-2" />
                        Utilisateur
                      </>
                    )}
                  </Badge>
                </div>

                {/* Quick Stats */}
                <div className="flex gap-4">
                  <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4 text-center">
                    <div className="text-3xl font-bold">{myOffers.length}</div>
                    <div className="text-xs text-orange-100">Offres</div>
                  </div>
                  <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4 text-center">
                    <div className="text-3xl font-bold">{myOffers.filter(o => o.status === 'approved').length}</div>
                    <div className="text-xs text-orange-100">Approuvées</div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Profile Info */}
            <div className="lg:col-span-2 space-y-6">
              <Card className="border-none shadow-xl">
                <CardHeader>
                  <CardTitle>Informations Personnelles</CardTitle>
                  <CardDescription>
                    {isOwnProfile ? 'Mettez à jour vos informations' : `Profil de ${displayedUser.name}`}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {isOwnProfile ? (
                    <form onSubmit={handleSaveProfile} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Nom complet *</Label>
                        <div className="relative">
                          <User className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                          <Input
                            id="name"
                            value={profileData.name}
                            onChange={(e) => handleChange('name', e.target.value)}
                            className="pl-10"
                            required
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label htmlFor="email">Email *</Label>
                          <div className="flex items-center gap-2">
                            {privacy.showEmail ? <Eye className="w-4 h-4 text-emerald-500" /> : <EyeOff className="w-4 h-4 text-gray-400" />}
                            <Label htmlFor="showEmail" className="text-xs text-gray-500 cursor-pointer">
                              Visible publiquement
                            </Label>
                            <Switch
                              id="showEmail"
                              checked={privacy.showEmail}
                              onCheckedChange={(checked) => setPrivacy(prev => ({ ...prev, showEmail: checked }))}
                            />
                          </div>
                        </div>
                        <div className="relative">
                          <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                          <Input
                            id="email"
                            type="email"
                            value={profileData.email}
                            onChange={(e) => handleChange('email', e.target.value)}
                            className="pl-10"
                            required
                          />
                        </div>
                        {!privacy.showEmail && profileData.email && (
                          <p className="text-xs text-orange-600 flex items-center gap-1">
                            <EyeOff className="w-3 h-3" />
                            Votre email sera masqué aux autres utilisateurs
                          </p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label htmlFor="phone">Téléphone</Label>
                          <div className="flex items-center gap-2">
                            {privacy.showPhone ? <Eye className="w-4 h-4 text-emerald-500" /> : <EyeOff className="w-4 h-4 text-gray-400" />}
                            <Label htmlFor="showPhone" className="text-xs text-gray-500 cursor-pointer">
                              Visible publiquement
                            </Label>
                            <Switch
                              id="showPhone"
                              checked={privacy.showPhone}
                              onCheckedChange={(checked) => setPrivacy(prev => ({ ...prev, showPhone: checked }))}
                            />
                          </div>
                        </div>
                        <div className="relative">
                          <Phone className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                          <Input
                            id="phone"
                            type="tel"
                            value={profileData.phone}
                            onChange={(e) => handleChange('phone', e.target.value)}
                            className="pl-10"
                            placeholder="06 12 34 56 78"
                          />
                        </div>
                        {!privacy.showPhone && profileData.phone && (
                          <p className="text-xs text-orange-600 flex items-center gap-1">
                            <EyeOff className="w-3 h-3" />
                            Votre téléphone sera masqué aux autres utilisateurs
                          </p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="website">Site web</Label>
                        <div className="relative">
                          <LinkIcon className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                          <Input
                            id="website"
                            type="url"
                            value={profileData.website}
                            onChange={(e) => handleChange('website', e.target.value)}
                            className="pl-10"
                            placeholder="https://..."
                          />
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="address">Adresse complète</Label>
                        <div className="flex items-center gap-2">
                          {privacy.showAddress ? <Eye className="w-4 h-4 text-emerald-500" /> : <EyeOff className="w-4 h-4 text-gray-400" />}
                          <Label htmlFor="showAddress" className="text-xs text-gray-500 cursor-pointer">
                            Visible publiquement
                          </Label>
                          <Switch
                            id="showAddress"
                            checked={privacy.showAddress}
                            onCheckedChange={(checked) => setPrivacy(prev => ({ ...prev, showAddress: checked }))}
                          />
                        </div>
                      </div>
                      <AddressAutocomplete
                        value={profileData.address}
                        onChange={(value) => handleChange('address', value)}
                        placeholder="15 Rue des Jardins, 94230 Cachan"
                      />
                      {!privacy.showAddress && profileData.address && (
                        <p className="text-xs text-orange-600 flex items-center gap-1">
                          <EyeOff className="w-3 h-3" />
                          Votre adresse sera masquée aux autres utilisateurs
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="bio">Biographie</Label>
                      <Textarea
                        id="bio"
                        value={profileData.bio}
                        onChange={(e) => handleChange('bio', e.target.value)}
                        rows={4}
                        placeholder="Parlez-nous de vous..."
                      />
                    </div>

                    {/* Compétences / Spécialités */}
                    <div className="space-y-3 p-4 bg-purple-50 rounded-lg border border-purple-200">
                      <Label className="text-base font-semibold text-purple-900">
                        Mes Compétences / Spécialités
                      </Label>
                      <p className="text-sm text-gray-600">
                        Sélectionnez les domaines dans lesquels vous pouvez aider la communauté
                      </p>
                      <MultiCategorySelector
                        selectedCompetences={profileData.competences}
                        onChange={(competences) => handleChange('competences', competences)}
                      />
                    </div>

                    {/* Avatar Selector - Simple Version */}
                    <Card className="border-2 border-dashed border-purple-300 bg-purple-50/50">
                      <CardContent className="pt-6">
                        <div className="space-y-4">
                          <Label className="text-base font-semibold flex items-center gap-2">
                            <User className="w-5 h-5" />
                            Choisissez votre avatar
                          </Label>
                          
                          <AvatarSelector
                            userName={user?.name || 'User'}
                            onSelect={handleAvatarSelect}
                            selectedAvatar={user.profile_picture_url || user.avatar}
                          />
                        </div>
                      </CardContent>
                    </Card>

                    <Button
                      type="submit"
                      className="w-full bg-gradient-to-r from-purple-600 via-red-500 to-yellow-500 hover:from-purple-700 hover:via-red-600 hover:to-yellow-600"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Sauvegarder les Modifications
                    </Button>
                  </form>
                  ) : (
                    /* Read-only view for other users */
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Nom complet</Label>
                          <p className="text-lg font-medium">{displayedUser.name || 'Non renseigné'}</p>
                        </div>
                        <div className="space-y-2">
                          <Label>Email</Label>
                          <p className="text-lg font-medium">{displayedUser.email}</p>
                        </div>
                      </div>
                      {displayedUser.phone && (
                        <div className="space-y-2">
                          <Label>Téléphone</Label>
                          <p className="text-lg font-medium">{displayedUser.phone}</p>
                        </div>
                      )}
                      {displayedUser.address && (
                        <div className="space-y-2">
                          <Label>Adresse</Label>
                          <p className="text-lg font-medium">{displayedUser.address}</p>
                        </div>
                      )}
                      {displayedUser.bio && (
                        <div className="space-y-2">
                          <Label>Bio</Label>
                          <p className="text-base">{displayedUser.bio}</p>
                        </div>
                      )}
                      {displayedUser.competences && Array.isArray(displayedUser.competences) && displayedUser.competences.length > 0 && (
                        <div className="space-y-2">
                          <Label>Compétences / Spécialités</Label>
                          <div className="flex flex-wrap gap-2">
                            {displayedUser.competences.map((comp, index) => (
                              <Badge
                                key={index}
                                variant="secondary"
                                className="px-3 py-1.5"
                              >
                                {comp.categoryName || comp.name || 'Compétence'}
                                {comp.subcategories && comp.subcategories.length > 0 && (
                                  <span className="ml-1 text-xs opacity-75">
                                    ({comp.subcategories.length})
                                  </span>
                                )}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      {displayedUser.website && (
                        <div className="space-y-2">
                          <Label>Site web</Label>
                          <a href={displayedUser.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                            {displayedUser.website}
                          </a>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Change Password - Only for own profile */}
              {isOwnProfile && (
              <Card className="border-none shadow-xl">
                <CardHeader>
                  <CardTitle>Sécurité</CardTitle>
                  <CardDescription>Changer votre mot de passe</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleChangePassword} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="currentPassword">Mot de passe actuel</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                        <Input
                          id="currentPassword"
                          type="password"
                          value={currentPassword}
                          onChange={(e) => setCurrentPassword(e.target.value)}
                          className="pl-10"
                          placeholder="••••••••"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="newPassword">Nouveau mot de passe</Label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                          <Input
                            id="newPassword"
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            className="pl-10"
                            placeholder="••••••••"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="confirmPassword">Confirmer</Label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                          <Input
                            id="confirmPassword"
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            className="pl-10"
                            placeholder="••••••••"
                          />
                        </div>
                      </div>
                    </div>

                    <Button
                      type="submit"
                      className="w-full bg-gradient-to-r from-emerald-500 to-blue-500 hover:from-emerald-600 hover:to-blue-600"
                    >
                      <Lock className="w-4 h-4 mr-2" />
                      Changer le Mot de Passe
                    </Button>
                  </form>
                </CardContent>
              </Card>
              )}
              {/* End of own profile only section */}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Activity Stats */}
              <Card className="border-none shadow-xl">
                <CardHeader>
                  <CardTitle>Activité</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 p-4 rounded-lg">
                    <div className="text-3xl font-bold text-emerald-600">{myOffers.length}</div>
                    <p className="text-sm text-gray-600 mt-1">Offres totales</p>
                  </div>
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-lg">
                    <div className="text-3xl font-bold text-blue-600">
                      {myOffers.filter(o => o.status === 'approved').length}
                    </div>
                    <p className="text-sm text-gray-600 mt-1">Approuvées</p>
                  </div>
                  <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded-lg">
                    <div className="text-3xl font-bold text-orange-600">
                      {myOffers.filter(o => o.status === 'pending').length}
                    </div>
                    <p className="text-sm text-gray-600 mt-1">En attente</p>
                  </div>
                </CardContent>
              </Card>

              {/* Quick Actions */}
              <Card className="border-none shadow-xl">
                <CardHeader>
                  <CardTitle>Actions Rapides</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <Button 
                    onClick={() => navigate('/ajouter')} 
                    className="w-full justify-start" 
                    variant="outline"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Ajouter une offre
                  </Button>
                  {['owner', 'admin', 'moderator'].includes(user.role) && (
                    <Button 
                      onClick={() => navigate('/dashboard')} 
                      className="w-full justify-start bg-gradient-to-r from-orange-500 to-red-500 text-white hover:from-orange-600 hover:to-red-600"
                    >
                      <Shield className="w-4 h-4 mr-2" />
                      Tableau de bord
                    </Button>
                  )}
                </CardContent>
              </Card>

              {/* Admin Actions - Only for viewing other users' profiles */}
              {!isOwnProfile && user.role === 'owner' && (
                <Card className="border-none shadow-xl border-2 border-purple-200">
                  <CardHeader>
                    <CardTitle className="text-purple-600 flex items-center gap-2">
                      <Shield className="w-5 h-5" />
                      Actions Administrateur
                    </CardTitle>
                    <CardDescription>Gérer les permissions de {displayedUser.name}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {/* Current role badge */}
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-sm text-gray-600 mb-2">Rôle actuel</p>
                      <Badge className="capitalize">
                        {displayedUser.role === 'admin' ? 'Administrateur' :
                         displayedUser.role === 'moderator' ? 'Modérateur' : 'Utilisateur'}
                      </Badge>
                    </div>

                    {/* Promote/Demote actions */}
                    {displayedUser.role === 'user' && (
                      <>
                        <Button 
                          onClick={() => handlePromoteUser(displayedUser.id, 'moderator')}
                          variant="outline"
                          className="w-full justify-start border-purple-500 text-purple-600 hover:bg-purple-50"
                        >
                          <UserCog className="w-4 h-4 mr-2" />
                          Promouvoir Modérateur
                        </Button>
                        <Button 
                          onClick={() => handlePromoteUser(displayedUser.id, 'admin')}
                          variant="outline"
                          className="w-full justify-start border-blue-500 text-blue-600 hover:bg-blue-50"
                        >
                          <Crown className="w-4 h-4 mr-2" />
                          Promouvoir Administrateur
                        </Button>
                      </>
                    )}

                    {displayedUser.role === 'moderator' && (
                      <>
                        <Button 
                          onClick={() => handlePromoteUser(displayedUser.id, 'admin')}
                          variant="outline"
                          className="w-full justify-start border-blue-500 text-blue-600 hover:bg-blue-50"
                        >
                          <Crown className="w-4 h-4 mr-2" />
                          Promouvoir Administrateur
                        </Button>
                        <Button 
                          onClick={() => handleRevokeRole(displayedUser.id)}
                          variant="outline"
                          className="w-full justify-start border-orange-500 text-orange-600 hover:bg-orange-50"
                        >
                          <X className="w-4 h-4 mr-2" />
                          Révoquer (Utilisateur)
                        </Button>
                      </>
                    )}

                    {displayedUser.role === 'admin' && (
                      <Button 
                        onClick={() => handleRevokeRole(displayedUser.id)}
                        variant="outline"
                        className="w-full justify-start border-orange-500 text-orange-600 hover:bg-orange-50"
                      >
                        <X className="w-4 h-4 mr-2" />
                        Révoquer (Utilisateur)
                      </Button>
                    )}

                    {/* Danger zone */}
                    <div className="border-t pt-3 mt-3">
                      <p className="text-xs text-gray-500 mb-2">Zone dangereuse</p>
                      <Button 
                        onClick={() => {
                          if (window.confirm(`Êtes-vous sûr de vouloir bloquer ${displayedUser.name} ?`)) {
                            toast.warning('Blocage', { description: 'Fonctionnalité en cours de développement' });
                          }
                        }}
                        variant="outline"
                        className="w-full justify-start border-red-500 text-red-600 hover:bg-red-50"
                      >
                        <Ban className="w-4 h-4 mr-2" />
                        Bloquer l'utilisateur
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Message button - For all users viewing other profiles */}
              {!isOwnProfile && (
                <Card className="border-none shadow-xl">
                  <CardHeader>
                    <CardTitle className="text-emerald-600 flex items-center gap-2">
                      <Mail className="w-5 h-5" />
                      Contact
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <Button 
                        onClick={() => navigate('/messages', { state: { openConversationWith: displayedUser } })}
                        className="w-full justify-start bg-gradient-to-r from-emerald-500 to-blue-500 text-white hover:from-emerald-600 hover:to-blue-600 border-0"
                      >
                        <Mail className="w-4 h-4 mr-2" />
                        Envoyer un message
                      </Button>
                      <a href={`mailto:${displayedUser.email}?subject=Contact depuis Alliance Solidarité`} className="block">
                        <Button 
                          variant="outline"
                          className="w-full justify-start"
                        >
                          <Mail className="w-4 h-4 mr-2" />
                          📧 Email direct
                        </Button>
                      </a>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Profile;
